import posthog from '@leyan/lytics';
import { useMemoizedFn } from 'ahooks';
import { NimContext } from 'components/Nim';
import { isNil, set } from 'lodash-es';
import { useContext } from 'react';
import { useTranslate } from '@tolgee/react';
import { uploadFileByURL, UploadFileByURLBody } from 'services/api/resource';
import { deleteResourceMessage, getResourceMessage } from 'services/cache/resourceMessage';
import { uploadLogInfoMap } from 'services/api/uploads';
import { logger } from 'services/logger';

const useSendFileURL = () => {
  const im = useContext(NimContext);
  const { on } = im;
  const { t } = useTranslate();

  // send file by url
  const send = useMemoizedFn((data: UploadFileByURLBody) => {
    posthog.capture('$startUpload', {
      type: 'sendFileURL',
      data,
    });
    return new Promise<{
      resourceId: number;
      url: string;
      localUrl: string;
      externalId?: string;
      mimeType?: string;
    }>((resolve, reject) => {
      uploadFileByURL(data)
        .then((response) => {
          logger.info(
            `[ResourceUpload]: ${JSON.stringify({
              type: 'uploadFileByURLResponse',
              data: response,
            })}`,
          );

          if (response?.data?.data) {
            const { id, url, resourceState, localUrl, externalVideoId, mimeType } =
              response.data.data;

            if (resourceState === 12) {
              const error = new Error('http resourceState === 12');

              set(error, 'failCause', {
                failType: 'A',
                code: 'upload_failed',
                desc: '',
                failMsgMap: {
                  'zh-CN': t('ViewsChat.上传失败'),
                  'en-US': t('ViewsChat.上传失败'),
                },
              } as Conversation.FailContent);

              reject(error);

              return;
            }

            if ((url || localUrl) && (resourceState === 8 || resourceState === undefined)) {
              resolve({
                resourceId: id,
                url,
                localUrl,
                externalId: externalVideoId,
                mimeType,
              });

              return;
            }

            const handleMessage = (
              message: Resource.Message,
              handler?: (message: Resource.Message) => void,
            ) => {
              if (!message) {
                return false;
              }

              const { resourceId, resourceState, url, externalVideoId } = message;

              if (!resourceId) {
                return false;
              }

              logger.info(
                `[ResourceUpload]: ${JSON.stringify({
                  type: 'yunxinMessage-useResourceUpload',
                  data: message,
                })}`,
              );

              if (isNil(id) || `${resourceId}` !== `${id}`) {
                return false;
              }

              const uploadInfo = uploadLogInfoMap[resourceId];
              delete uploadLogInfoMap[resourceId];

              if (resourceState === 8) {
                resolve({
                  resourceId: id,
                  url,
                  localUrl,
                  externalId: externalVideoId,
                  mimeType,
                });

                handler?.(message);

                if (uploadInfo) {
                  const uploadPlatformTime = Date.now();
                  const uploadPlatformDuration = uploadPlatformTime - uploadInfo.uploadOssTime;
                  posthog.capture('$uploadInfo', {
                    ...uploadInfo,
                    uploadPlatformTime,
                    uploadPlatformDuration,
                  });
                }

                return true;
              }

              if (resourceState === 12) {
                const error = new Error('message resourceState === 12');

                set(error, 'failCause', {
                  failType: 'A',
                  code: 'upload_failed',
                  desc: '',
                  failMsgMap: {
                    'zh-CN': t('ViewsChat.上传失败'),
                    'en-US': t('ViewsChat.上传失败'),
                  },
                } as Conversation.FailContent);

                reject(error);

                handler?.(message);

                return true;
              }

              return false;
            };

            const handled = handleMessage(getResourceMessage(id), (message) => {
              logger.info(
                `[ResourceUpload]: ${JSON.stringify({
                  type: 'yunxin-before-http',
                  data: message,
                })}`,
              );
            });

            deleteResourceMessage(id);

            if (handled) {
              return;
            }

            let timer: any;

            const off = on('message', (message: Resource.Message) =>
              handleMessage(message, () => {
                off();

                clearTimeout(timer);
              }),
            );

            timer = setTimeout(() => {
              off();

              const error = new Error('Timeout');

              set(error, 'failCause', {
                failType: 'A',
                code: 'upload_timeout',
                desc: '',
                failMsgMap: {
                  'zh-CN': '上传超时',
                  'en-US': 'Upload timed out',
                },
              } as Conversation.FailContent);

              reject(error);
            }, 5 * 60 * 1000);
          }
        })
        .catch((err) => {
          const error = new Error(err);

          set(error, 'failCause', {
            failType: 'A',
            code: 'upload_failed',
            desc: '',
            failMsgMap: {
              'zh-CN': t('ViewsChat.上传失败'),
              'en-US': t('ViewsChat.上传失败'),
            },
          } as Conversation.FailContent);

          reject(error);
        });
    }).catch((err) => {
      posthog.capture('$uploadFailed', {
        type: 'sendFileURL',
        error: String(err),
      });
      return Promise.reject(err);
    });
  });

  return send;
};

export default useSendFileURL;
